import { formatDateBR, parseSalario, formatBRL } from "../utils.js";

/**
 * Constrói as seções de revisão do TCA CIEEMG Empregador.
 *
 * @param {object} params
 * @param {object} params.formData - Dados do formulário
 * @param {string} [params.reviewRole="empresa"] - Papel ativo ("empresa", "estudante", "ciee")
 * @param {boolean} [params.isEstudante=false] - Se o usuário logado é estudante
 * @param {object} [params.horario={}] - Estrutura de horários (fixo/alternado)
 * @param {string} [params.localAtividade=""] - Identificador do local (MATRIZ/id filial)
 * @param {boolean} [params.mostrarFiliais=false] - Flag se a empresa possui filiais
 * @returns {Array<{ id: string, section: string, fields: Array<{ label: string, value: any, complete: boolean }> }>}
 */
export const buildReviewDataTcaEmpregador = ({
  formData = {},
  reviewRole = "empresa",
  isEstudante = false,
  horario = {},
  localAtividade = "",
  mostrarFiliais = false,
} = {}) => {
  const cpfLimpo = (formData.cpf || "").replace(/\D/g, "");

  const isReviewEmpresa =
    reviewRole === "empresa" || (!isEstudante && reviewRole !== "estudante");
  const isReviewEstudante = reviewRole === "estudante";
  const isReviewCiee = !isEstudante && reviewRole === "ciee";

  const showEmpregador = isReviewEmpresa;
  const showAprendiz = isReviewEmpresa || isReviewEstudante || isReviewCiee;
  const showLocalFilial = isReviewEmpresa;
  const showVT = isReviewEmpresa;
  const showDependentes = isReviewEmpresa;

  const empregadorFields = [
    {
      label: "Nome do representante legal",
      value: formData.nome_representante_legal,
      complete: !!formData.nome_representante_legal?.trim(),
    },
    {
      label: "Cargo",
      value: formData.cargo_representante_legal,
      complete: !!formData.cargo_representante_legal?.trim(),
    },
    {
      label: "Data início contrato",
      value: formatDateBR(formData.data_inicio_contrato),
      complete: !!formData.data_inicio_contrato,
    },
    {
      label: "Salário",
      value: formatBRL(formData.salario),
      complete: !!formData.salario && parseSalario(formData.salario) > 0,
    },
    {
      label: "Curso",
      value: formData.curso,
      complete: !!formData.curso?.trim(),
    },
  ];

  if (formData.telefone_empresa?.trim()) {
    empregadorFields.push({
      label: "Telefone Empresa",
      value: formData.telefone_empresa,
      complete: true,
    });
  }
  if (formData.email_empresa?.trim()) {
    empregadorFields.push({
      label: "E-mail Empresa",
      value: formData.email_empresa,
      complete: true,
    });
  }
  if (
    formData.nome_tutor?.trim() ||
    formData.tutor_aprendiz_empresa?.trim()
  ) {
    empregadorFields.push({
      label: "Tutor do Aprendiz",
      value: formData.nome_tutor || formData.tutor_aprendiz_empresa,
      complete: true,
    });
  }
  if (formData.email_tutor?.trim()) {
    empregadorFields.push({
      label: "E-mail do Tutor",
      value: formData.email_tutor,
      complete: true,
    });
  }
  if (formData.atividades_aprendiz?.trim()) {
    empregadorFields.push({
      label: "Atividades do Aprendiz na Empresa",
      value: formData.atividades_aprendiz,
      complete: true,
    });
  }
  if (formData.endereco_aprendizagem_pratica?.trim()) {
    empregadorFields.push({
      label: "Endereço Completo da Aprendizagem Prática",
      value: formData.endereco_aprendizagem_pratica,
      complete: true,
    });
  }
  if (horario?.tipo) {
    const tipoLabel =
      horario.tipo === "fixo"
        ? "Fixo"
        : horario.tipo === "alternado"
        ? "Alternado"
        : horario.tipo;
    empregadorFields.push({
      label: "Horário da Aprendizagem Prática",
      value: tipoLabel,
      complete: !!tipoLabel,
    });
    if (horario.tipo === "fixo" && horario.fixo) {
      if (horario.fixo.entrada) {
        empregadorFields.push({
          label: "Horário de Entrada",
          value: horario.fixo.entrada,
          complete: true,
        });
      }
      if (horario.fixo.saida) {
        empregadorFields.push({
          label: "Horário de Saída",
          value: horario.fixo.saida,
          complete: true,
        });
      }
    }
  }

  const aprendizFields = [
    {
      label: "Nome completo",
      value: formData.nome_completo,
      complete: !!formData.nome_completo?.trim(),
    },
    {
      label: "CPF (11 dígitos)",
      value: formData.cpf,
      complete: cpfLimpo.length === 11,
    },
  ];

  if (formData.telefone_01?.trim()) {
    aprendizFields.push({
      label: "Telefone do Aprendiz",
      value: formData.telefone_01,
      complete: true,
    });
  }
  if (formData.email_pessoal?.trim()) {
    aprendizFields.push({
      label: "E-mail Pessoal",
      value: formData.email_pessoal,
      complete: true,
    });
  }

  const aprendizEnderecoFields = [];
  if (formData.cep?.trim()) {
    aprendizEnderecoFields.push({
      label: "CEP",
      value: formData.cep,
      complete: true,
    });
  }
  if (formData.logradouro?.trim()) {
    aprendizEnderecoFields.push({
      label: "Logradouro",
      value: formData.logradouro,
      complete: true,
    });
  }
  if (formData.numero_endereco?.trim()) {
    aprendizEnderecoFields.push({
      label: "Nº",
      value: formData.numero_endereco,
      complete: true,
    });
  }
  if (formData.complemento?.trim()) {
    aprendizEnderecoFields.push({
      label: "Complemento",
      value: formData.complemento,
      complete: true,
    });
  }
  if (formData.bairro?.trim()) {
    aprendizEnderecoFields.push({
      label: "Bairro",
      value: formData.bairro,
      complete: true,
    });
  }
  if (formData.cidade?.trim()) {
    aprendizEnderecoFields.push({
      label: "Cidade",
      value: formData.cidade,
      complete: true,
    });
  }
  if (formData.estado_endereco?.trim()) {
    aprendizEnderecoFields.push({
      label: "UF",
      value: formData.estado_endereco,
      complete: true,
    });
  }
  if (formData.telefone_02?.trim()) {
    aprendizEnderecoFields.push({
      label: "Telefone 02",
      value: formData.telefone_02,
      complete: true,
    });
  }

  const aprendizEscolaridadeFields = [];
  if (formData.grau_instrucao?.trim()) {
    aprendizEscolaridadeFields.push({
      label: "Escolaridade",
      value: formData.grau_instrucao,
      complete: true,
    });
  }
  if (formData.curso_escolaridade?.trim()) {
    aprendizEscolaridadeFields.push({
      label: "Curso Escolaridade",
      value: formData.curso_escolaridade,
      complete: true,
    });
  }
  if (formData.instituicao_ensino?.trim()) {
    aprendizEscolaridadeFields.push({
      label: "Instituição de Ensino",
      value: formData.instituicao_ensino,
      complete: true,
    });
  }
  if (formData.conclusao?.trim()) {
    aprendizEscolaridadeFields.push({
      label: "Conclusão (ou previsão)",
      value: formData.conclusao,
      complete: true,
    });
  }

  const aprendizDocumentosFields = [];
  if (formData.rg?.trim()) {
    aprendizDocumentosFields.push({
      label: "RG",
      value: formData.rg,
      complete: true,
    });
  }
  if (formData.pis?.trim()) {
    aprendizDocumentosFields.push({
      label: "PIS",
      value: formData.pis,
      complete: true,
    });
  }
  if (formData.data_emissao_rg?.trim()) {
    aprendizDocumentosFields.push({
      label: "Data Emissão RG",
      value: formatDateBR(formData.data_emissao_rg),
      complete: true,
    });
  }
  if (formData.orgao_emissor_rg?.trim()) {
    aprendizDocumentosFields.push({
      label: "Órgão Emissor RG",
      value: formData.orgao_emissor_rg,
      complete: true,
    });
  }
  if (formData.estado_rg?.trim()) {
    aprendizDocumentosFields.push({
      label: "Estado RG",
      value: formData.estado_rg,
      complete: true,
    });
  }
  if (formData.titulo_eleitor?.trim()) {
    aprendizDocumentosFields.push({
      label: "Título de Eleitor",
      value: formData.titulo_eleitor,
      complete: true,
    });
  }
  if (formData.zona?.trim()) {
    aprendizDocumentosFields.push({
      label: "Zona",
      value: formData.zona,
      complete: true,
    });
  }
  if (formData.secao?.trim()) {
    aprendizDocumentosFields.push({
      label: "Seção",
      value: formData.secao,
      complete: true,
    });
  }
  if (formData.data_emissao_titulo?.trim()) {
    aprendizDocumentosFields.push({
      label: "Data Emissão Título",
      value: formatDateBR(formData.data_emissao_titulo),
      complete: true,
    });
  }

  const aprendizContaFields = [];
  if (formData.agencia?.trim()) {
    aprendizContaFields.push({
      label: "Agência",
      value: formData.agencia,
      complete: true,
    });
  }
  if (formData.div1?.trim()) {
    aprendizContaFields.push({
      label: "DIV",
      value: formData.div1,
      complete: true,
    });
  }
  if (formData.conta?.trim()) {
    aprendizContaFields.push({
      label: "Conta",
      value: formData.conta,
      complete: true,
    });
  }
  if (formData.div2?.trim()) {
    aprendizContaFields.push({
      label: "DIV",
      value: formData.div2,
      complete: true,
    });
  }

  const aprendizPessoalFields = [];
  if (formData.data_nascimento?.trim()) {
    aprendizPessoalFields.push({
      label: "Data de Nascimento",
      value: formatDateBR(formData.data_nascimento),
      complete: true,
    });
  }
  if (formData.nome_social?.trim()) {
    aprendizPessoalFields.push({
      label: "Nome Social",
      value: formData.nome_social,
      complete: true,
    });
  }
  if (formData.estado_natal?.trim()) {
    aprendizPessoalFields.push({
      label: "Estado Natal",
      value: formData.estado_natal,
      complete: true,
    });
  }
  if (formData.naturalidade?.trim()) {
    aprendizPessoalFields.push({
      label: "Naturalidade",
      value: formData.naturalidade,
      complete: true,
    });
  }
  if (formData.nacionalidade?.trim()) {
    aprendizPessoalFields.push({
      label: "Nacionalidade",
      value: formData.nacionalidade,
      complete: true,
    });
  }
  if (formData.sexo?.trim()) {
    const sexoLabel =
      formData.sexo === "M"
        ? "Masculino"
        : formData.sexo === "F"
        ? "Feminino"
        : formData.sexo;
    aprendizPessoalFields.push({
      label: "Sexo",
      value: sexoLabel,
      complete: true,
    });
  }
  if (formData.tipo_sanguineo?.trim()) {
    aprendizPessoalFields.push({
      label: "Tipo Sanguíneo",
      value: formData.tipo_sanguineo,
      complete: true,
    });
  }
  if (formData.cor?.trim()) {
    aprendizPessoalFields.push({
      label: "Cor",
      value: formData.cor,
      complete: true,
    });
  }
  if (formData.estado_civil?.trim()) {
    aprendizPessoalFields.push({
      label: "Estado Civil",
      value: formData.estado_civil,
      complete: true,
    });
  }

  const aprendizMaeFields = [];
  if (formData.nome_mae?.trim()) {
    aprendizMaeFields.push({
      label: "Nome da Mãe",
      value: formData.nome_mae,
      complete: true,
    });
  }
  if (formData.cpf_mae?.trim()) {
    aprendizMaeFields.push({
      label: "CPF da Mãe",
      value: formData.cpf_mae,
      complete: true,
    });
  }
  if (formData.telefone_mae?.trim()) {
    aprendizMaeFields.push({
      label: "Telefone da Mãe",
      value: formData.telefone_mae,
      complete: true,
    });
  }
  if (formData.email_mae?.trim()) {
    aprendizMaeFields.push({
      label: "E-mail da Mãe",
      value: formData.email_mae,
      complete: true,
    });
  }
  if (formData.data_nascimento_mae?.trim()) {
    aprendizMaeFields.push({
      label: "Data Nascimento Mãe",
      value: formatDateBR(formData.data_nascimento_mae),
      complete: true,
    });
  }
  if (formData.naturalidade_mae?.trim()) {
    aprendizMaeFields.push({
      label: "Naturalidade da Mãe",
      value: formData.naturalidade_mae,
      complete: true,
    });
  }
  if (formData.estado_civil_mae?.trim()) {
    aprendizMaeFields.push({
      label: "Estado Civil da Mãe",
      value: formData.estado_civil_mae,
      complete: true,
    });
  }

  const aprendizMilitarFields = [];
  if (formData.numero_certificado_reservista?.trim()) {
    aprendizMilitarFields.push({
      label: "Nº Certificado de Reservista",
      value: formData.numero_certificado_reservista,
      complete: true,
    });
  }
  if (formData.data_emissao_certificado?.trim()) {
    aprendizMilitarFields.push({
      label: "Data Emissão Certificado",
      value: formatDateBR(formData.data_emissao_certificado),
      complete: true,
    });
  }
  if (formData.orgao_expedicao?.trim()) {
    aprendizMilitarFields.push({
      label: "Órgão Expedição",
      value: formData.orgao_expedicao,
      complete: true,
    });
  }
  if (formData.situacao_militar?.trim()) {
    aprendizMilitarFields.push({
      label: "Situação Militar",
      value: formData.situacao_militar,
      complete: true,
    });
  }

  const sections = [];

  if (showEmpregador) {
    sections.push({
      id: "empregador",
      section: "Dados do Empregador",
      fields: empregadorFields,
    });
  }

  if (showAprendiz) {
    sections.push({
      id: "aprendiz-dados",
      section: "Dados do(a) Aprendiz",
      fields: aprendizFields,
    });
    if (aprendizPessoalFields.length > 0) {
      sections.push({
        id: "aprendiz-pessoal",
        section: "Identificação Pessoal do Aprendiz",
        fields: aprendizPessoalFields,
      });
    }
    if (aprendizDocumentosFields.length > 0) {
      sections.push({
        id: "aprendiz-documentos",
        section: "Documentos",
        fields: aprendizDocumentosFields,
      });
    }
    if (aprendizMaeFields.length > 0) {
      sections.push({
        id: "aprendiz-mae",
        section: "Dados da Mãe",
        fields: aprendizMaeFields,
      });
    }
    if (aprendizEnderecoFields.length > 0) {
      sections.push({
        id: "aprendiz-endereco",
        section: "Endereço Principal do Aprendiz",
        fields: aprendizEnderecoFields,
      });
    }
    if (aprendizEscolaridadeFields.length > 0) {
      sections.push({
        id: "aprendiz-escolaridade",
        section: "Escolaridade",
        fields: aprendizEscolaridadeFields,
      });
    }
    if (aprendizMilitarFields.length > 0) {
      sections.push({
        id: "aprendiz-militar",
        section: "Situação Militar",
        fields: aprendizMilitarFields,
      });
    }
    if (aprendizContaFields.length > 0) {
      sections.push({
        id: "aprendiz-conta",
        section: "Conta Bancária",
        fields: aprendizContaFields,
      });
    }
  }

  if (showLocalFilial) {
    sections.push({
      id: "local",
      section: "Local de Atividade",
      fields: [
        {
          label: "Local de realização das atividades",
          value:
            localAtividade === "FILIAL"
              ? "Filial"
              : localAtividade || "MATRIZ",
          complete: !mostrarFiliais || !!localAtividade,
        },
      ],
    });

    if (mostrarFiliais && localAtividade && localAtividade !== "MATRIZ") {
      sections.push({
        id: "filial",
        section: "Informações da Filial",
        fields: [
          {
            label: "Nome da Filial",
            value: formData.nome_filial,
            complete: !!formData.nome_filial?.trim(),
          },
          {
            label: "CPF/CNPJ",
            value: formData.cpfCnpj,
            complete: !!formData.cpfCnpj?.trim(),
          },
          {
            label: "Município",
            value: formData.enderecoFilial_municipio,
            complete: !!formData.enderecoFilial_municipio?.trim(),
          },
          {
            label: "UF",
            value: formData.enderecoFilial_uf,
            complete: !!formData.enderecoFilial_uf?.trim(),
          },
          {
            label: "Telefone 1",
            value: formData.telefones,
            complete: !!formData.telefones?.trim(),
          },
          {
            label: "Telefone 2",
            value: formData.telefones2,
            complete: true,
          },
          {
            label: "E-mail Contrato",
            value: formData.emailContratos,
            complete: !!formData.emailContratos?.trim(),
          },
          {
            label: "E-mail Financeiro",
            value: formData.emailFinanceiro,
            complete: !!formData.emailFinanceiro?.trim(),
          },
          {
            label: "Representante por",
            value: formData.representadaPor,
            complete: !!formData.representadaPor?.trim(),
          },
          {
            label: "Cargo do Representante",
            value: formData.cargoRepresentante,
            complete: !!formData.cargoRepresentante?.trim(),
          },
          {
            label: "E-mail do Representante",
            value: formData.emailRepresentante,
            complete: !!formData.emailRepresentante?.trim(),
          },
          {
            label: "Supervisor do Estágio",
            value: formData.responsavelSupervisor,
            complete: !!formData.responsavelSupervisor?.trim(),
          },
          {
            label: "Cargo do Supervisor",
            value: formData.cargoSupervisor,
            complete: !!formData.cargoSupervisor?.trim(),
          },
          {
            label: "E-mail do Supervisor",
            value: formData.emailSupervisor,
            complete: !!formData.emailSupervisor?.trim(),
          },
        ],
      });
    }
  }

  if (
    showVT &&
    (formData.deseja_vale_transporte === true || formData.deseja_vale_transporte === 1 || formData.deseja_vale_transporte === "true" || formData.deseja_vale_transporte === "Sim" || formData.endereco_vt_logradouro || formData.endereco_residencial_vt || formData.servicos_meios_transporte)
  ) {
    sections.push({
      id: "vt",
      section: "Vale Transporte",
      fields: [
        ...(formData.endereco_vt_logradouro
          ? [
              {
                label: "Endereço Residencial VT",
                value: [
                  formData.endereco_vt_logradouro,
                  formData.endereco_vt_numero,
                  formData.endereco_vt_complemento,
                  formData.endereco_vt_bairro,
                  formData.endereco_vt_cidade,
                  formData.endereco_vt_uf,
                  formData.endereco_vt_cep,
                ]
                  .filter(Boolean)
                  .join(", "),
                complete: true,
              },
            ]
          : []),
        ...([
          ["Prática (Ida)", formData.transportes_pratica_ida],
          ["Prática (Volta)", formData.transportes_pratica_volta],
          ["Teoria (Ida)", formData.transportes_teoria_ida],
          ["Teoria (Volta)", formData.transportes_teoria_volta],
        ].flatMap(([titulo, lista]) =>
          Array.isArray(lista)
            ? lista
                .filter((i) => i?.linha || i?.tarifa)
                .map((i, idx) => ({
                  label: `${titulo} #${idx + 1}`,
                  value: `${i.linha || "-"} (${formatBRL(i.tarifa)})`,
                  complete: true,
                }))
            : [],
        )),
        ...(formData.endereco_residencial_vt
          ? [
              {
                label: "Endereço Residencial VT",
                value: formData.endereco_residencial_vt,
                complete: true,
              },
            ]
          : []),
        ...(formData.servicos_meios_transporte
          ? [
              {
                label: "Meios de Transporte",
                value: formData.servicos_meios_transporte,
                complete: true,
              },
            ]
          : []),
        ...(formData.linha_pratica
          ? [
              {
                label: "Linha Prática",
                value: formData.linha_pratica,
                complete: true,
              },
            ]
          : []),
        ...(formData.tarifa_pratica
          ? [
              {
                label: "Tarifa Prática",
                value: formatBRL(formData.tarifa_pratica),
                complete: true,
              },
            ]
          : []),
      ],
    });
  }

  if (
    showDependentes &&
    (formData.dependente_1_nome ||
      formData.dependente_2_nome ||
      formData.dependente_3_nome)
  ) {
    sections.push({
      id: "dependentes",
      section: "Dependentes para IR",
      fields: [
        ...(formData.dependente_1_nome
          ? [
              {
                label: "Dependente 1",
                value: formData.dependente_1_nome,
                complete: true,
              },
              {
                label: "CPF Dep 1",
                value: formData.dependente_1_cpf || "Não informado",
                complete: true,
              },
            ]
          : []),
        ...(formData.dependente_2_nome
          ? [
              {
                label: "Dependente 2",
                value: formData.dependente_2_nome,
                complete: true,
              },
              {
                label: "CPF Dep 2",
                value: formData.dependente_2_cpf || "Não informado",
                complete: true,
              },
            ]
          : []),
        ...(formData.dependente_3_nome
          ? [
              {
                label: "Dependente 3",
                value: formData.dependente_3_nome,
                complete: true,
              },
              {
                label: "CPF Dep 3",
                value: formData.dependente_3_cpf || "Não informado",
                complete: true,
              },
            ]
          : []),
      ],
    });
  }

  return sections;
};
